import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HealthStorages,
  StorageStatus,
} from "../../../api/types/CamerasAdminVM";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { useFormatSDCardMutation } from "../../../api/network/vmApiService";
import { toast } from "react-toastify";
import Check from "../../../assets/svg/check-ring.svg";
import Warning from "../../../assets/svg/warning.svg";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import ModalFormatConfirmation from "./modalFormatSDCard";

interface ScreenCameraStorageProps {
  dataStorageSummary: HealthStorages | null;
  cameraId: string;
}

interface StorageComponentProps {
  id: string;
  title: string;
  storage: StorageStatus | undefined;
  onClick?: (id: string) => void;
}

const StorageComponent: FC<StorageComponentProps> = ({
  id,
  title,
  storage,
  onClick,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix:
      "camera.dashboard.management.cameras.camera_details.tabs.storage",
  });
  const parseStorage = () => {
    if (!storage || !storage?.storage.includes("/"))
      return { used: "0", total: "0" };
    const [used, total] = storage?.storage?.split("/")?.map((s) => s.trim());
    return { used, total };
  };

  const { used, total } = parseStorage();
  const usedPercentage = Math.round((Number(used) / Number(total)) * 100);

  const SdCardInfo = [
    { title: "SD Card Id", value: storage?.id },
    {
      title: "SD Card Health",
      value: storage?.status === "Good" ? t("healthy") : t("needs_attentation"),
      icon: storage?.status === "Good" ? Check : Warning,
    },
    {
      title: "Endurance Life Ratio",
      value: !isNaN(Number(storage?.life_percentage))
        ? `${storage?.life_percentage}%`
        : storage?.life_percentage,
    },
  ];
  return (
    <>
      <div className="w-full flex justify-between">
        <h1 className="font-semibold text-sm leading-5 tracking-tight">
          {title}
        </h1>
        {id !== "INTERNAL" && (
          <AdminFormFieldSubmit
            label={t("format")}
            variant="secondary"
            onClick={() => onClick?.(id)}
          />
        )}
      </div>
      <div className="w-full flex flex-col gap-2">
        <div className="flex justify-between font-normal text-sm leading-5 tracking-tight">
          <span>{`${used} ${t("gb_used")}`}</span>
          <span>{`${t("total")} ${total} ${t("gb")}`}</span>
        </div>
        <div className="w-full h-8 rounded-lg bg-white">
          <div
            className="bg-accent-blue-bright h-full rounded-lg z-10 relative"
            style={{ width: usedPercentage }}
          />
        </div>
      </div>
      {id !== "INTERNAL" &&
        SdCardInfo?.map((item, index) => (
          <div
            key={index}
            className="font-semibold text-sm leading-5 tracking-tight align-middle flex flex-col gap-2"
          >
            <h1>{item.title}</h1>
            <div className="flex items-center gap-2">
              {item.icon && <img src={item.icon} alt="Check" />}
              <span className="font-normal">{item.value}</span>
            </div>
          </div>
        ))}
      <div className="w-full flex flex-col gap-2"></div>
    </>
  );
};

const ScreenCameraStorage: FC<ScreenCameraStorageProps> = ({
  cameraId,
  dataStorageSummary,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix:
      "camera.dashboard.management.cameras.camera_details.tabs.storage",
  });
  const [formatId, setFormatId] = useState<string>("");
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  // driver mutation
  const [formatSDCardApiTrigger] = useFormatSDCardMutation();
  const handleFormat = () => {
    console.log(`Formatting ${formatId}...`);
    if (formatId === "SDCARD" || formatId === "SDCARD_2") {
      formatSDCardApiTrigger({ cameraId, type: formatId })
        .unwrap()
        .then((response) => {
          if (response.message === "Success") {
            toast.success(t("format_success"));
          } else {
            toast.error(t("format_failed"));
          }
        })
        .catch((error) => {
          console.error("Error formatting SD card:", error);
          toast.error(error?.join(" ") || t("format_failed"));
        });
    }
  };
  return (
    <div className="col-span-9 space-y-4">
      <h1 className="font-semibold text-base leading-none align-middle col-span-9">
        {t("storage_information")}
      </h1>
      <div className="bg-gray-100 col-span-9 rounded-lg p-4 mt-2 space-y-6">
        <StorageComponent
          id="INTERNAL"
          title={t("internal_storage")}
          storage={dataStorageSummary?.internal}
        />
        <div className="border-b border-dotted border-gray-400 col-span-9 my-2" />
        <StorageComponent
          id="SDCARD"
          title={t("sd_card_1")}
          storage={dataStorageSummary?.sd_card_1}
          onClick={(id) => {
            setFormatId(id);
            dispatch(
              setModalsData({ ...modalsState, showModalFormatSDCard: true })
            );
          }}
        />
        {dataStorageSummary?.sd_card_2 && (
          <StorageComponent
            id="SDCARD_2"
            title={t("sd_card_2")}
            storage={dataStorageSummary?.sd_card_2}
            onClick={(id) => {
              setFormatId(id);
              dispatch(
                setModalsData({ ...modalsState, showModalFormatSDCard: true })
              );
            }}
          />
        )}
      </div>
      <ModalFormatConfirmation handleFormatSDCard={handleFormat} />
    </div>
  );
};

export default ScreenCameraStorage;
